import { useCallback, useRef, useState } from "react";

const ACTIVE_PHASES = new Set(["pending", "preparing", "downloading", "saving"]);

/**
 * Hàng chờ tải file tuần tự — tránh trình duyệt chặn khi trigger nhiều download cùng lúc.
 */
export function useCertificateDownloadQueue({ gapMs = 500, successClearMs = 5000 } = {}) {
  const [jobs, setJobs] = useState([]);
  const queueRef = useRef([]);
  const runningRef = useRef(false);

  const updateJob = useCallback((id, patch) => {
    setJobs((prev) => prev.map((job) => (job.id === id ? { ...job, ...patch } : job)));
  }, []);

  const drainQueue = useCallback(async () => {
    if (runningRef.current) return;
    runningRef.current = true;

    while (queueRef.current.length > 0) {
      const { id, execute } = queueRef.current.shift();
      updateJob(id, { phase: "preparing", progress: null, error: null });

      try {
        await execute({
          onProgress: (patch) => updateJob(id, patch),
        });
        updateJob(id, { phase: "success", progress: 100 });

        if (successClearMs > 0) {
          window.setTimeout(() => {
            setJobs((prev) => prev.filter((job) => job.id !== id || job.phase !== "success"));
          }, successClearMs);
        }
      } catch (err) {
        updateJob(id, {
          phase: "error",
          error: err?.message || "Không thể tải file.",
        });
      }

      if (queueRef.current.length > 0 && gapMs > 0) {
        await new Promise((resolve) => window.setTimeout(resolve, gapMs));
      }
    }

    runningRef.current = false;
  }, [gapMs, successClearMs, updateJob]);

  const enqueue = useCallback(
    (job) => {
      const id =
        job.id ||
        `dl-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
      const entry = {
        id,
        title: job.title || "Tải file",
        subtitle: job.subtitle || "",
        fileName: job.fileName || "",
        totalItems: job.totalItems ?? null,
        meta: job.meta || null,
        phase: "pending",
        progress: null,
        loadedBytes: 0,
        totalBytes: 0,
        error: null,
      };

      setJobs((prev) => [...prev, entry]);
      queueRef.current.push({ id, execute: job.execute });
      void drainQueue();
      return id;
    },
    [drainQueue]
  );

  const clearFinished = useCallback(() => {
    setJobs((prev) => prev.filter((job) => ACTIVE_PHASES.has(job.phase)));
  }, []);

  const isBusy = jobs.some((job) => ACTIVE_PHASES.has(job.phase));
  const pendingCount = jobs.filter((job) => job.phase === "pending").length;
  const activeCount = jobs.filter(
    (job) => job.phase !== "pending" && ACTIVE_PHASES.has(job.phase)
  ).length;
  const successCount = jobs.filter((job) => job.phase === "success").length;
  const errorCount = jobs.filter((job) => job.phase === "error").length;

  const isJobActive = useCallback(
    (jobId) => {
      const job = jobs.find((item) => item.id === jobId);
      return job ? ACTIVE_PHASES.has(job.phase) : false;
    },
    [jobs]
  );

  return {
    jobs,
    enqueue,
    isBusy,
    pendingCount,
    activeCount,
    successCount,
    errorCount,
    clearFinished,
    isJobActive,
  };
}
